// ============================================================
// Notifications — role-aware activity feed (client-side demo data)
// ============================================================
import type { IconName } from '../components/ui/Icon'

export type NotifTone = 'ok' | 'warn' | 'bad' | 'info'

export type NotifAction =
  | { type: 'review'; teacherId: string }
  | { type: 'page'; page: 'overview' | 'sections' | 'forms' | 'dashboard' }
  | { type: 'domain'; domain: number; code: string }

export interface NotifItem {
  id: string
  icon: IconName
  tone: NotifTone
  title: string
  body: string
  time: string
  group: string
  unread: boolean
  go: NotifAction
}

export function buildNotifications(role: 'teacher' | 'admin'): NotifItem[] {
  if (role === 'admin') {
    return [
      { id: 'n-a1', icon: 'inbox', tone: 'info', title: 'Priya Nair submitted her Spring Summative', body: 'Ready for your review — score components and complete Sections 5–6.', time: '25m ago', group: 'Today', unread: true, go: { type: 'review', teacherId: 't-0' } },
      { id: 'n-a2', icon: 'warn', tone: 'bad', title: 'Ben Castillo is overdue', body: 'Fall Self-Reflection was due Oct 15 and is only 28% complete.', time: '2h ago', group: 'Today', unread: true, go: { type: 'review', teacherId: 't-3' } },
      { id: 'n-a3', icon: 'warn', tone: 'bad', title: 'Marcus Webb Jr. has not started', body: 'No phase begun. First-year teacher — consider reaching out.', time: '3h ago', group: 'Today', unread: true, go: { type: 'review', teacherId: 't-5' } },
      { id: 'n-a4', icon: 'award', tone: 'ok', title: 'Hannah Boyd signed her summative evaluation', body: 'Evaluation complete and routed to HR.', time: 'Yesterday', group: 'Earlier', unread: false, go: { type: 'review', teacherId: 't-4' } },
      { id: 'n-a5', icon: 'refresh', tone: 'info', title: 'Walkthrough data imported', body: '3 classroom walkthroughs synced as evidence across your roster.', time: 'Tue', group: 'Earlier', unread: false, go: { type: 'page', page: 'forms' } },
      { id: 'n-a6', icon: 'cal', tone: 'warn', title: 'Spring Summative cycle closes Mar 6', body: '5 of your 8 certified staff still have open plans.', time: 'Mon', group: 'Earlier', unread: false, go: { type: 'page', page: 'dashboard' } },
    ]
  }
  return [
    { id: 'n-t1', icon: 'award', tone: 'ok', title: 'Maren Holloway signed your evaluation', body: 'Your Spring Summative is finalized. You may attach a written response within 10 working days.', time: '1h ago', group: 'Today', unread: true, go: { type: 'page', page: 'sections' } },
    { id: 'n-t2', icon: 'pen', tone: 'info', title: 'Comment on 2e · Organizing Physical Space', body: '“Love the esports locker idea — let’s document the grant in your evidence.” — M. Holloway', time: '4h ago', group: 'Today', unread: true, go: { type: 'domain', domain: 2, code: '2e' } },
    { id: 'n-t3', icon: 'clock', tone: 'warn', title: 'Spring Summative due Mar 6, 2026', body: 'Your plan is 88% complete — finish Section 4 evidence to wrap up.', time: 'Today', group: 'Today', unread: true, go: { type: 'page', page: 'overview' } },
    { id: 'n-t4', icon: 'refresh', tone: 'info', title: 'Walkthrough added to your evidence', body: 'Feb 3 walkthrough imported and attached to Domain 3.', time: 'Yesterday', group: 'Earlier', unread: false, go: { type: 'domain', domain: 3, code: '3a' } },
    { id: 'n-t5', icon: 'check', tone: 'ok', title: 'Midyear Check-In accepted', body: 'Maren Holloway reviewed your midyear reflection. No action needed.', time: 'Jan 24', group: 'Earlier', unread: false, go: { type: 'page', page: 'overview' } },
  ]
}

const NOTIF_READ_STORE = 'growthlane-notif-read-v1'

export function loadReadIds(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(NOTIF_READ_STORE) || '[]'))
  } catch {
    return new Set()
  }
}

export function saveReadIds(set: Set<string>) {
  try {
    localStorage.setItem(NOTIF_READ_STORE, JSON.stringify([...set]))
  } catch {
    /* ignore */
  }
}

export function notifUnread(role: 'teacher' | 'admin'): number {
  const read = loadReadIds()
  return buildNotifications(role).filter((n) => n.unread && !read.has(n.id)).length
}

export const NOTIF_TONE: Record<NotifTone, string> = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  info: 'var(--navy)',
}
export const NOTIF_TONE_BG: Record<NotifTone, string> = {
  ok: 'var(--ok-bg)',
  warn: 'var(--warn-bg)',
  bad: 'var(--bad-bg)',
  info: 'var(--navy-tint)',
}
